import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  X,
  Plus,
  Loader2,
  Trash2,
  AlertCircle,
  Edit2,
  Check,
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

const BulkOperationsModal = ({
  open,
  onOpenChange,
  mode, // 'edit' or 'add'
  selectedQuestions = [],
  onSubmit,
  questionTypes,
  categories,
  questionBanks,
  isLoading = false,
  selectedQuestionBankId = null,
  currentQuestionBank, // Add this prop
}) => {
  const [bulkQuestions, setBulkQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedQuestionBank, setSelectedQuestionBank] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [hasTriggeredValidation, setHasTriggeredValidation] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);

  const combinedQuestionBanks = [
    ...(questionBanks?.banks || []),
    ...(questionBanks?.companyQuestionBanks || []),
    ...(questionBanks?.purchasedQuestionBanks || []),
  ];

  const showToast = (title, description, variant = "default") => {
    setToastMessage({ title, description, variant });
    setTimeout(() => setToastMessage(null), 3000);
  };

  useEffect(() => {
    if (selectedQuestionBankId) {
      const bank = combinedQuestionBanks.find(
        (qb) => qb._id === selectedQuestionBankId
      );
      setSelectedQuestionBank(bank);
    }
  }, [selectedQuestionBankId, combinedQuestionBanks]);

  useEffect(() => {
    if (mode === "add") {
      setBulkQuestions([createEmptyQuestion()]);
    } else if (mode === "edit" && selectedQuestions.length > 0) {
      setBulkQuestions(
        selectedQuestions.map((q) => ({
          ...q,
          choices: q.choices || [],
          tags: Array.isArray(q.tags)
            ? q.tags
            : q.tags
            ? q.tags.split(",").map((t) => t.trim())
            : [],
        }))
      );
    }
    setCurrentQuestionIndex(0);
    setValidationErrors({});
    setHasTriggeredValidation(false);
  }, [mode, selectedQuestions, open]);

  const createEmptyQuestion = () => ({
    question: "",
    questionType: selectedQuestionBank?.questionType?.[0] || "",
    category: selectedQuestionBank?.category || "",
    subCategory: selectedQuestionBank?.subcategory || "",
    questionBankId: selectedQuestionBankId || "",
    value: 1,
    difficultyLevel: "",
    answers: [],
    choices: [],
    tags: [],
    isNew: true,
  });

  const validateQuestion = (question, index) => {
    const errors = [];

    // Check mandatory fields
    if (!question.question?.trim()) {
      errors.push("Question is required");
    }
    if (!question.difficultyLevel) {
      errors.push("Difficulty level is required");
    }

    // Check minimum choices (3)
    if (!question.choices || question.choices.length < 3) {
      errors.push("Minimum 3 answer choices are required");
    }

    // Check minimum answers (1)
    if (!question.answers || question.answers.length < 1) {
      errors.push("At least 1 correct answer must be selected");
    }

    return errors;
  };

  const validateAllQuestions = () => {
    const errors = {};
    bulkQuestions.forEach((question, index) => {
      const questionErrors = validateQuestion(question, index);
      if (questionErrors.length > 0) {
        errors[index] = questionErrors;
      }
    });
    setValidationErrors(errors);
    setHasTriggeredValidation(true);
    return Object.keys(errors).length === 0;
  };

  const checkCapacity = (questionsToAdd) => {
    if (!currentQuestionBank) return { canAdd: true, message: "" };

    const currentCount = currentQuestionBank.questionCount || 0;
    const maxCapacity = currentQuestionBank.maxCapacity || 5000;
    const availableSpace = maxCapacity - currentCount;

    if (questionsToAdd > availableSpace) {
      return {
        canAdd: false,
        message: `Cannot add ${questionsToAdd} questions. Only ${availableSpace} slots available.`,
      };
    }

    return { canAdd: true, message: "" };
  };

  const addNewQuestion = () => {
    const capacityCheck = checkCapacity(bulkQuestions.length + 1);
    if (!capacityCheck.canAdd) {
      showToast("Capacity Exceeded", capacityCheck.message, "destructive");
      return;
    }
    setBulkQuestions([...bulkQuestions, createEmptyQuestion()]);
    setCurrentQuestionIndex(bulkQuestions.length);
  };

  const removeQuestion = (index) => {
    if (bulkQuestions.length > 1) {
      const newQuestions = bulkQuestions.filter((_, i) => i !== index);
      setBulkQuestions(newQuestions);

      // Clear validation errors for removed question and adjust indices
      if (hasTriggeredValidation) {
        const newErrors = {};
        Object.keys(validationErrors).forEach((key) => {
          const errorIndex = parseInt(key);
          if (errorIndex < index) {
            newErrors[errorIndex] = validationErrors[key];
          } else if (errorIndex > index) {
            newErrors[errorIndex - 1] = validationErrors[key];
          }
        });
        setValidationErrors(newErrors);
      }

      if (currentQuestionIndex >= newQuestions.length) {
        setCurrentQuestionIndex(newQuestions.length - 1);
      }
    }
  };

  const updateCurrentQuestion = (field, value) => {
    const updatedQuestions = [...bulkQuestions];
    updatedQuestions[currentQuestionIndex] = {
      ...updatedQuestions[currentQuestionIndex],
      [field]: value,
    };
    setBulkQuestions(updatedQuestions);

    // Only re-validate if validation has been triggered before
    if (hasTriggeredValidation) {
      const questionErrors = validateQuestion(
        updatedQuestions[currentQuestionIndex],
        currentQuestionIndex
      );
      const newErrors = { ...validationErrors };
      if (questionErrors.length > 0) {
        newErrors[currentQuestionIndex] = questionErrors;
      } else {
        delete newErrors[currentQuestionIndex];
      }
      setValidationErrors(newErrors);
    }
  };

  const updateChoices = (choices) => {
    updateCurrentQuestion("choices", choices);
  };

  const updateTags = (tags) => {
    updateCurrentQuestion("tags", tags);
  };

  const handleSubmit = () => {
    if (mode === "add") {
      const capacityCheck = checkCapacity(bulkQuestions.length);
      if (!capacityCheck.canAdd) {
        showToast("Capacity Exceeded", capacityCheck.message, "destructive");
        return;
      }

      // Validate all questions before submitting
      if (!validateAllQuestions()) {
        showToast("Validation Error", "Please fix all validation errors before submitting.", "destructive");
        return;
      }
    } else {
      // For edit mode, also validate
      if (!validateAllQuestions()) {
        showToast("Validation Error", "Please fix all validation errors before submitting.", "destructive");
        return;
      }
    }

    // Process questions to ensure proper format
    const processedQuestions = bulkQuestions.map((q) => ({
      ...q,
      questionType:
        typeof q.questionType === "string"
          ? q.questionType
          : Array.isArray(q.questionType)
          ? q.questionType[0]
          : "",
      tags: Array.isArray(q.tags) ? q.tags : [],
    }));

    onSubmit(processedQuestions);
  };

  const handleClose = () => {
    onOpenChange(false);
    setBulkQuestions([]);
    setCurrentQuestionIndex(0);
    setValidationErrors({});
    setHasTriggeredValidation(false);
  };

  const currentQuestion =
    bulkQuestions[currentQuestionIndex] || createEmptyQuestion();

  const hasErrors =
    hasTriggeredValidation && Object.keys(validationErrors).length > 0;

  return (
    <>
      {toastMessage && (
        <div className="fixed top-4 right-4 z-50 bg-white border rounded-lg shadow-lg p-4 max-w-sm">
          <div className={`flex items-center gap-2 ${toastMessage.variant === 'destructive' ? 'text-red-600' : 'text-green-600'}`}>
            <AlertCircle className="h-4 w-4" />
            <div>
              <div className="font-semibold">{toastMessage.title}</div>
              <div className="text-sm">{toastMessage.description}</div>
            </div>
          </div>
        </div>
      )}
      
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {mode === "edit" ? "Bulk Edit Questions" : "Bulk Add Questions"}
            </DialogTitle>
            <DialogDescription>
              {mode === "edit"
                ? `Edit ${bulkQuestions.length} selected question(s)`
                : "Add multiple questions at once"}
            </DialogDescription>
          </DialogHeader>

          {hasErrors && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Please fix validation errors in questions:{" "}
                {Object.keys(validationErrors)
                  .map((i) => parseInt(i) + 1)
                  .join(", ")}
              </AlertDescription>
            </Alert>
          )}

          <div className="flex gap-6">
            {/* Question Navigation */}
            <div className="w-1/4 border-r pr-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold">
                  Questions ({bulkQuestions.length})
                </h3>
                {mode === "add" && (
                  <Button size="sm" onClick={addNewQuestion}>
                    <Plus className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {bulkQuestions.map((q, index) => (
                  <div
                    key={index}
                    className={`p-2 border rounded cursor-pointer flex justify-between items-center ${
                      index === currentQuestionIndex
                        ? "bg-blue-50 border-blue-200"
                        : "hover:bg-gray-50"
                    } ${
                      hasTriggeredValidation && validationErrors[index]
                        ? "border-red-200 bg-red-50"
                        : ""
                    }`}
                    onClick={() => setCurrentQuestionIndex(index)}
                  >
                    <span className="text-sm truncate">
                      {q.question || `Question ${index + 1}`}
                      {hasTriggeredValidation && validationErrors[index] && (
                        <AlertCircle className="inline ml-1 h-3 w-3 text-red-500" />
                      )}
                    </span>
                    {mode === "add" && bulkQuestions.length > 1 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeQuestion(index);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Question Form */}
            <div className="flex-1">
              <QuestionForm
                question={currentQuestion}
                onUpdate={(field, value) => updateCurrentQuestion(field, value)}
                onUpdateChoices={updateChoices}
                onUpdateTags={updateTags}
                questionTypes={questionTypes}
                categories={categories}
                questionBanks={combinedQuestionBanks}
                selectedQuestionBankId={selectedQuestionBankId}
                selectedQuestionBank={selectedQuestionBank}
                mode={mode}
                validationErrors={
                  hasTriggeredValidation
                    ? validationErrors[currentQuestionIndex] || []
                    : []
                }
                showToast={showToast}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {mode === "edit" ? "Update Questions" : "Create Questions"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const QuestionForm = ({
  question,
  onUpdate,
  onUpdateChoices,
  onUpdateTags,
  questionTypes,
  categories,
  questionBanks,
  selectedQuestionBankId,
  selectedQuestionBank,
  mode,
  validationErrors = [],
  showToast,
}) => {
  const [newChoice, setNewChoice] = useState("");
  const [newTag, setNewTag] = useState("");
  const [editingChoiceIndex, setEditingChoiceIndex] = useState(null);
  const [editingChoiceText, setEditingChoiceText] = useState("");

  const handleAddChoice = () => {
    if (newChoice.trim()) {
      const newChoices = [...(question.choices || []), newChoice.trim()];
      onUpdateChoices(newChoices);
      setNewChoice("");

      showToast(
        "Choice Added",
        "New answer choice has been added successfully."
      );
    } else {
      showToast(
        "Invalid Choice",
        "Please enter a valid choice text.",
        "destructive"
      );
    }
  };

  const handleRemoveChoice = (index) => {
    // Ensure minimum 3 choices
    if (question.choices.length <= 3) {
      showToast(
        "Cannot Remove Choice",
        "Minimum 3 answer choices are required. Cannot remove more choices.",
        "destructive"
      );
      return;
    }

    const removedChoice = question.choices[index];
    const newChoices = question.choices.filter((_, i) => i !== index);
    onUpdateChoices(newChoices);

    // Remove from answers if it was selected
    if (question.answers && question.answers.includes(removedChoice)) {
      const newAnswers = question.answers.filter(
        (answer) => answer !== removedChoice
      );
      onUpdate("answers", newAnswers);
    }

    showToast(
      "Choice Removed",
      "Answer choice has been removed successfully."
    );
  };

  const handleEditChoice = (index) => {
    setEditingChoiceIndex(index);
    setEditingChoiceText(question.choices[index]);
  };

  const handleSaveChoice = (index) => {
    if (editingChoiceText.trim()) {
      const oldChoice = question.choices[index];
      const newChoices = [...question.choices];
      newChoices[index] = editingChoiceText.trim();
      onUpdateChoices(newChoices);

      // Update answers if the old choice was selected
      if (question.answers && question.answers.includes(oldChoice)) {
        const newAnswers = question.answers.map((answer) =>
          answer === oldChoice ? editingChoiceText.trim() : answer
        );
        onUpdate("answers", newAnswers);
      }

      setEditingChoiceIndex(null);
      setEditingChoiceText("");

      showToast(
        "Choice Updated",
        "Answer choice has been updated successfully."
      );
    } else {
      showToast(
        "Invalid Choice",
        "Please enter a valid choice text.",
        "destructive"
      );
    }
  };

  const handleCancelEdit = () => {
    setEditingChoiceIndex(null);
    setEditingChoiceText("");
  };

  const handleAddTag = () => {
    if (newTag.trim() && !question.tags.includes(newTag.trim())) {
      const newTags = [...(question.tags || []), newTag.trim()];
      onUpdateTags(newTags);
      setNewTag("");

      showToast(
        "Tag Added",
        "New tag has been added successfully."
      );
    } else if (question.tags.includes(newTag.trim())) {
      showToast(
        "Duplicate Tag",
        "This tag already exists.",
        "destructive"
      );
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    const newTags = question.tags.filter((tag) => tag !== tagToRemove);
    onUpdateTags(newTags);

    showToast(
      "Tag Removed",
      "Tag has been removed successfully."
    );
  };

  const handleAnswerChange = (choice, checked, isRadio = false) => {
    let newAnswers;
    if (isRadio) {
      newAnswers = [choice];
    } else {
      newAnswers = checked
        ? [...(question.answers || []), choice]
        : (question.answers || []).filter((answer) => answer !== choice);
    }
    onUpdate("answers", newAnswers);
  };

  const handleQuestionBankChange = (value) => {
    onUpdate("questionBankId", value);
    const bank = questionBanks.find((qb) => qb._id === value);
    if (bank) {
      onUpdate("category", bank.category);
      onUpdate("subCategory", bank.subcategory);
      onUpdate(
        "questionType",
        Array.isArray(bank.questionType)
          ? bank.questionType[0]
          : bank.questionType
      );
    }
  };

  const selectedQuestionType = questionTypes?.questionTypes?.find(
    (qt) => qt.identifier === question.questionType
  );
  const isMultipleChoice =
    selectedQuestionType?.identifier === "MULTIPLE_CHOICE";

  const getFieldError = (field) => {
    return validationErrors.find((error) =>
      error.toLowerCase().includes(field.toLowerCase())
    );
  };

  return (
    <div className="space-y-4">
      {validationErrors.length > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <ul className="list-disc list-inside">
              {validationErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Left Column */}
        <div className="space-y-4">
          <div>
            <Label htmlFor="questionBankId">Question Bank *</Label>
            <Select
              value={question.questionBankId}
              onValueChange={handleQuestionBankChange}
              disabled={!!selectedQuestionBankId}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select Question Bank" />
              </SelectTrigger>
              <SelectContent>
                {questionBanks.map((bank) => (
                  <SelectItem key={bank._id} value={bank._id}>
                    {bank.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="questionType">Question Type *</Label>
            <Input
              value={question.questionType}
              readOnly
              className="bg-gray-100 cursor-not-allowed"
              placeholder="Question type will be auto-populated"
            />
          </div>

          <div>
            <Label htmlFor="category">Category *</Label>
            <Input
              value={question.category}
              readOnly
              className="bg-gray-100 cursor-not-allowed"
              placeholder="Category will be auto-populated"
            />
          </div>

          <div>
            <Label htmlFor="subCategory">Sub Category *</Label>
            <Input
              value={question.subCategory}
              readOnly
              className="bg-gray-100 cursor-not-allowed"
              placeholder="Sub category will be auto-populated"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="value">Points *</Label>
              <Input
                type="number"
                min="1"
                value={question.value}
                onChange={(e) => onUpdate("value", parseInt(e.target.value))}
              />
            </div>

            <div>
              <Label
                htmlFor="difficultyLevel"
                className={getFieldError("difficulty") ? "text-red-500" : ""}
              >
                Difficulty *
              </Label>
              <Select
                value={question.difficultyLevel}
                onValueChange={(value) => onUpdate("difficultyLevel", value)}
              >
                <SelectTrigger
                  className={
                    getFieldError("difficulty") ? "border-red-500" : ""
                  }
                >
                  <SelectValue placeholder="Select Difficulty" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="easy">Easy</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="hard">Hard</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          <div>
            <Label
              htmlFor="question"
              className={getFieldError("question") ? "text-red-500" : ""}
            >
              Question *
            </Label>
            <Textarea
              value={question.question}
              onChange={(e) => onUpdate("question", e.target.value)}
              placeholder="Enter your question"
              rows={4}
              className={getFieldError("question") ? "border-red-500" : ""}
            />
          </div>

          {/* Tags */}
          <div>
            <Label>Tags</Label>
            <div className="space-y-2">
              <div className="flex gap-2">
                <Input
                  placeholder="Add a tag"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={(e) =>
                    e.key === "Enter" && (e.preventDefault(), handleAddTag())
                  }
                />
                <Button type="button" onClick={handleAddTag} size="sm">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {question.tags &&
                  question.tags.map((tag, index) => (
                    <Badge
                      key={index}
                      variant="secondary"
                      className="flex items-center gap-1"
                    >
                      {tag}
                      <X
                        className="h-3 w-3 cursor-pointer hover:text-red-500"
                        onClick={() => handleRemoveTag(tag)}
                      />
                    </Badge>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <Label className={getFieldError("choices") ? "text-red-500" : ""}>
            Answer Choices (Minimum 3 required)
          </Label>
          <div className="space-y-2">
            <div className="flex gap-2">
              <Input
                placeholder="Add an answer choice"
                value={newChoice}
                onChange={(e) => setNewChoice(e.target.value)}
                onKeyPress={(e) =>
                  e.key === "Enter" && (e.preventDefault(), handleAddChoice())
                }
                className={getFieldError("choices") ? "border-red-500" : ""}
              />
              <Button type="button" onClick={handleAddChoice} size="sm">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {getFieldError("choices") && (
              <p className="text-sm text-red-500">
                Minimum 3 answer choices are required
              </p>
            )}
          </div>
        </div>

        {question.choices && question.choices.length > 0 && (
          <div>
            <Label className={getFieldError("answer") ? "text-red-500" : ""}>
              Select Correct Answer(s) (At least 1 required)
            </Label>
            <div className="space-y-2 max-h-40 overflow-y-auto border rounded-lg p-4">
              {isMultipleChoice ? (
                question.choices.map((choice, index) => (
                  <div
                    key={`choice-${index}-${choice}`}
                    className="flex items-center justify-between space-x-2"
                  >
                    <div className="flex items-center space-x-2 flex-1">
                      <Checkbox
                        checked={
                          question.answers && question.answers.includes(choice)
                        }
                        onCheckedChange={(checked) =>
                          handleAnswerChange(choice, checked)
                        }
                      />
                      {editingChoiceIndex === index ? (
                        <div className="flex items-center space-x-2 flex-1">
                          <Input
                            value={editingChoiceText}
                            onChange={(e) =>
                              setEditingChoiceText(e.target.value)
                            }
                            onKeyPress={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleSaveChoice(index);
                              } else if (e.key === "Escape") {
                                e.preventDefault();
                                handleCancelEdit();
                              }
                            }}
                            className="flex-1"
                            autoFocus
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSaveChoice(index)}
                            title="Save changes"
                          >
                            <Check className="h-4 w-4 text-green-600" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={handleCancelEdit}
                            title="Cancel editing"
                          >
                            <X className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      ) : (
                        <span
                          className="flex-1 cursor-pointer hover:text-blue-600"
                          onClick={() => handleEditChoice(index)}
                        >
                          {choice}
                        </span>
                      )}
                    </div>
                    {editingChoiceIndex !== index && (
                      <div className="flex items-center space-x-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditChoice(index)}
                          title="Edit choice"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveChoice(index)}
                          disabled={question.choices.length <= 3}
                          title={
                            question.choices.length <= 3
                              ? "Cannot remove - minimum 3 choices required"
                              : "Remove choice"
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <RadioGroup
                  value={(question.answers && question.answers[0]) || ""}
                  onValueChange={(value) =>
                    handleAnswerChange(value, true, true)
                  }
                >
                  {question.choices.map((choice, index) => (
                    <div
                      key={`radio-choice-${index}-${choice}`}
                      className="flex items-center justify-between space-x-2"
                    >
                      <div className="flex items-center space-x-2 flex-1">
                        <RadioGroupItem value={choice} />
                        {editingChoiceIndex === index ? (
                          <div className="flex items-center space-x-2 flex-1">
                            <Input
                              value={editingChoiceText}
                              onChange={(e) =>
                                setEditingChoiceText(e.target.value)
                              }
                              onKeyPress={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  handleSaveChoice(index);
                                } else if (e.key === "Escape") {
                                  e.preventDefault();
                                  handleCancelEdit();
                                }
                              }}
                              className="flex-1"
                              autoFocus
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSaveChoice(index)}
                              title="Save changes"
                            >
                              <Check className="h-4 w-4 text-green-600" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={handleCancelEdit}
                              title="Cancel editing"
                            >
                              <X className="h-4 w-4 text-red-600" />
                            </Button>
                          </div>
                        ) : (
                          <label
                            className="flex-1 cursor-pointer hover:text-blue-600"
                            onClick={() => handleEditChoice(index)}
                          >
                            {choice}
                          </label>
                        )}
                      </div>
                      {editingChoiceIndex !== index && (
                        <div className="flex items-center space-x-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditChoice(index)}
                            title="Edit choice"
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveChoice(index)}
                            disabled={question.choices.length <= 3}
                            title={
                              question.choices.length <= 3
                                ? "Cannot remove - minimum 3 choices required"
                                : "Remove choice"
                            }
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </RadioGroup>
              )}
            </div>
            {getFieldError("answer") && (
              <p className="text-sm text-red-500">
                At least 1 correct answer must be selected
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkOperationsModal;
